import { Component, Suspense, lazy, useCallback, useMemo, useState } from "react";

const LAZY_LOAD_ERROR_PATTERNS = [
  "chunkloaderror",
  "failed to fetch dynamically imported module",
  "failed to load module script",
  "importing a module script failed",
  "loading chunk",
];

export const isLazyLoadError = error => {
  const message = `${error?.name || ""} ${error?.message || ""}`.toLowerCase();
  return LAZY_LOAD_ERROR_PATTERNS.some(pattern => message.includes(pattern));
};

class LazyRouteErrorBoundary extends Component {
  state = {error: null};

  static getDerivedStateFromError(error) {
    return {error};
  }

  componentDidMount() {
    window.addEventListener("online", this.handleReconnect);
  }

  componentWillUnmount() {
    window.removeEventListener("online", this.handleReconnect);
  }

  componentDidCatch(error, errorInfo) {
    this.props.onError?.(error, errorInfo);
  }

  handleReconnect = () => {
    if (this.state.error && isLazyLoadError(this.state.error)) {
      this.props.onRetry();
    }
  };

  render() {
    const {error} = this.state;
    if (!error) return this.props.children;

    const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
    const title = isOffline ? "You're offline" : "This page could not be loaded";
    const message = isOffline
      ? "Reconnect to the internet and this page will retry automatically."
      : "The connection may have been interrupted. Try loading the page again.";

    return (
      <main
        role="alert"
        style={{
          minHeight: "50vh",
          display: "grid",
          placeItems: "center",
          padding: "2rem",
          textAlign: "center",
        }}
      >
        <div>
          <h1>{title}</h1>
          <p>{message}</p>
          <button type="button" onClick={this.props.onRetry}>
            Try again
          </button>
        </div>
      </main>
    );
  }
}

const RetryableLazyRoute = ({loader, loadingFallback = null, onError, routeKey = "route"}) => {
  const [attempt, setAttempt] = useState(0);
  const LazyComponent = useMemo(() => lazy(loader), [attempt, loader, routeKey]);
  const retry = useCallback(() => setAttempt(current => current + 1), []);

  return (
    <LazyRouteErrorBoundary key={`${routeKey}:${attempt}`} onError={onError} onRetry={retry}>
      <Suspense fallback={loadingFallback}>
        <LazyComponent />
      </Suspense>
    </LazyRouteErrorBoundary>
  );
};

export default RetryableLazyRoute;
